import React, { useState } from 'react';
import { Brush, CheckSquare, LayoutTemplate, Settings2, Workflow } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { useCompany } from '../../contexts/CompanyContext';
import { useAuth } from '../../contexts/AuthContext';
import CompanyFeaturesManager from './CompanyFeaturesManager';
import CompanyBrandingManager from './CompanyBrandingManager';
import CompanyWorkflowsManager from './CompanyWorkflowsManager';
import CompanyCustomFieldsManager from './CompanyCustomFieldsManager';
import CompanyNotificationTemplatesManager from './CompanyNotificationTemplatesManager';
import CompanySelector from '../CompanySelector';
import { useI18n } from '../../contexts/I18nContext';

type Tab = 'features' | 'branding' | 'workflows' | 'custom-fields' | 'templates';

const tabConfig: Record<
  Tab,
  {
    label: string;
    description: string;
    icon: LucideIcon;
  }
> = {
  features: {
    label: 'Feature Flags',
    description: 'Ative módulos, integrações e relatórios por empresa.',
    icon: Settings2,
  },
  branding: {
    label: 'Branding',
    description: 'Defina logos, cores e CSS personalizado.',
    icon: Brush,
  },
  workflows: {
    label: 'Workflows',
    description: 'Personalize fluxos de trabalho por módulo.',
    icon: Workflow,
  },
  'custom-fields': {
    label: 'Campos',
    description: 'Crie campos extras em formulários específicos.',
    icon: CheckSquare,
  },
  templates: {
    label: 'Templates',
    description: 'Gerencie modelos de email, SMS e WhatsApp.',
    icon: LayoutTemplate,
  },
};

const CompanyCustomizationManager: React.FC = () => {
  const { t } = useI18n();
  const { currentCompany } = useCompany();
  const { isAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState<Tab>('features');

  if (!currentCompany) {
    return (
      <div
        style={{
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
        }}
      >
        <p style={{ margin: 0 }}>{t('customization.selectCompany')}</p>
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div
        style={{
          padding: '20px',
          borderRadius: '8px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
        }}
      >
        <p style={{ margin: 0 }}>{t('customization.adminOnly')}</p>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
      <div
        style={{
          padding: '20px',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          backgroundColor: '#fff',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '16px',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        <div>
          <h2 style={{ margin: '0 0 6px 0' }}>{t('customization.title')}</h2>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '14px' }}>{t('customization.subtitle')}</p>
        </div>
        <CompanySelector />
      </div>

      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '12px',
        }}
      >
        {(Object.keys(tabConfig) as Tab[]).map((tab) => {
          const Icon = tabConfig[tab].icon;
          const isActive = tab === activeTab;
          return (
            <button
              key={tab}
              type="button"
              onClick={() => setActiveTab(tab)}
              style={{
                flex: 1,
                minWidth: '220px',
                textAlign: 'left',
                borderRadius: '12px',
                border: isActive ? '2px solid #4f46e5' : '1px solid #e5e7eb',
                backgroundColor: isActive ? '#eef2ff' : '#fff',
                padding: '16px',
                cursor: 'pointer',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                <Icon size={20} />
                <div>
                  <div style={{ fontWeight: 600 }}>{tabConfig[tab].label}</div>
                  <div style={{ color: '#6b7280', fontSize: '13px' }}>{tabConfig[tab].description}</div>
                </div>
              </div>
            </button>
          );
        })}
      </div>

      <div
        style={{
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '24px',
          backgroundColor: '#f9fafb',
        }}
      >
        {activeTab === 'features' && <CompanyFeaturesManager companyId={currentCompany.id} />}
        {activeTab === 'branding' && (
          <CompanyBrandingManager companyId={currentCompany.id} companyName={currentCompany.name} />
        )}
        {activeTab === 'workflows' && <CompanyWorkflowsManager companyId={currentCompany.id} />}
        {activeTab === 'custom-fields' && <CompanyCustomFieldsManager companyId={currentCompany.id} />}
        {activeTab === 'templates' && (
          <CompanyNotificationTemplatesManager companyId={currentCompany.id} />
        )}
      </div>
    </div>
  );
};

export default CompanyCustomizationManager;

